import {
  ChangeDetectorRef,
  Component,
  inject,
  signal,
  ChangeDetectionStrategy,
} from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';

import { VirtualScrollerModule } from '@iharbeck/ngx-virtual-scroller';
import { debounceTime, startWith, tap } from 'rxjs/operators';

import { injectTranslate, TranslatePipe } from '@core/i18n-legacy';
import { SelectTeamMembersService } from '@core/tellent-members-logic';
import { SelectMembersDialogData } from '@core/tellent-members-types';
import {
  AvatarComponent,
  BoxComponent,
  ButtonComponent,
  ButtonLoadingDirective,
  CheckboxComponent,
  DialogBodyComponent,
  DialogComponentI,
  DialogFooterComponent,
  DialogHeaderComponent,
  DialogRef,
  DividerComponent,
  EmptyStateComponent,
  FormFieldComponent,
  GroupComponent,
  HeadingComponent,
  IconComponent,
  InputComponent,
  RtTooltip,
  SpinnerComponent,
  TruncateComponent,
} from '@core/user-interface-legacy';
import { injectUntilDestroyed } from '@core/utils-legacy';

import { SelectTeamMembersController } from './select-team-members.controller';

@Component({
  selector: 'tla-select-team-members-dialog',
  imports: [
    BoxComponent,
    ButtonComponent,
    DialogBodyComponent,
    DialogFooterComponent,
    DialogHeaderComponent,
    DividerComponent,
    EmptyStateComponent,
    FormFieldComponent,
    GroupComponent,
    HeadingComponent,
    IconComponent,
    InputComponent,
    SpinnerComponent,
    VirtualScrollerModule,
    AvatarComponent,
    CheckboxComponent,
    TruncateComponent,
    ButtonLoadingDirective,
    RtTooltip,
    ReactiveFormsModule,
    TranslatePipe,
  ],
  templateUrl: './select-team-members-dialog.component.html',
  styleUrl: './select-team-members-dialog.component.less',
  changeDetection: ChangeDetectionStrategy.Eager,
  viewProviders: [SelectTeamMembersController, SelectTeamMembersService],
})
export class SelectTeamMembersDialogComponent implements DialogComponentI<
  SelectMembersDialogData,
  any
> {
  private readonly untilDestroyed = injectUntilDestroyed();
  private readonly dialogRef = inject(DialogRef);
  private readonly t = injectTranslate();
  protected readonly selectTeamMembersController = inject(SelectTeamMembersController);
  protected readonly cdRef = inject(ChangeDetectorRef);

  protected readonly queryControl = new FormControl('', { nonNullable: true });

  protected disabledIds = signal<Record<string, boolean>>({});
  protected saveLoading = signal(false);

  protected header = signal<string>(this.t('tlc.select_team_members_dialog.header'));
  protected subheader = signal<string>(this.t('tlc.select_team_members_dialog.subheader'));
  protected ctaButtonText = signal<string>('');

  constructor() {
    this.queryControl.valueChanges
      .pipe(
        tap(() => this.selectTeamMembersController.isLoading.set(true)),
        debounceTime(300),
        startWith(''),
        this.untilDestroyed,
      )
      .subscribe(value => {
        this.selectTeamMembersController.setQuery(value);
      });
  }

  public setDialogData(data: SelectMembersDialogData): void {
    this.disabledIds.set(data.membersIds.reduce((acc, guid) => ({ ...acc, [guid]: true }), {}));
    if (data.header) this.header.set(data.header);
    if (data.subheader) this.subheader.set(data.subheader);
    if (data.ctaButtonText) this.ctaButtonText.set(data.ctaButtonText);
  }

  protected submit() {
    this.saveLoading.set(true);
    this.dialogRef.close({ membersGuids: [...this.selectTeamMembersController.selectedIds] });
  }

  protected closeDialog(): void {
    this.dialogRef.close();
  }
}
